import {
  Box,
  Input,
  Text,
  VStack,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Tag,
  TagLabel,
  TagCloseButton,
  Tooltip,
  Switch,
  HStack,
  Radio,
  RadioGroup,
  Stack,
  useColorModeValue,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverHeader,
  PopoverBody,
  PopoverCloseButton,
  Portal,
} from "@chakra-ui/react";

import { ChevronDownIcon, CheckIcon } from "@chakra-ui/icons";
import { FiCalendar, FiChevronDown, FiFilter } from "react-icons/fi";
import { DateRangePicker } from "react-date-range";
import { format } from "date-fns";

import { useEffect, useState } from "react";
import { keyframes } from "@emotion/react";

import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

// ─── Types ────────────────────────────────────────────────────────────────────

export type FilterField =
  | { type: "text";        key: string; label: string; placeholder?: string }
  | { type: "numberRange"; key: string; label: string; min?: number; max?: number }
  | { type: "dateRange";   key: string; label: string }
  | { type: "multiSelect"; key: string; label: string; options: { label: string; value: string }[]; allLabel?: string }
  | { type: "select";      key: string; label: string; options: { label: string; value: string }[] }
  | { type: "toggle";      key: string; label: string; defaultValue?: boolean };

export interface SideFilterProps<T extends Record<string, any>> {
  filters: T;
  onChange: (filters: T) => void;
  onReset: () => void;
  fields: FilterField[];
  panelWidth?: number;
}

// ─── Inline utils (substitui FilterUtils externo) ─────────────────────────────

function deepClone<T>(obj: T): T { return JSON.parse(JSON.stringify(obj)); }

function isFiltersChanged(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) !== JSON.stringify(b);
}

// ─── Keyframes ────────────────────────────────────────────────────────────────

const pulse = keyframes`
  0%   { transform: scale(1);   opacity: 1;   }
  50%  { transform: scale(1.3); opacity: 0.6; }
  100% { transform: scale(1);   opacity: 1;   }
`;

// ─── Component ────────────────────────────────────────────────────────────────

export const SideFilter = <T extends Record<string, any>>({
  filters,
  onChange,
  onReset,
  fields,
  panelWidth = 320,
}: SideFilterProps<T>) => {
  const [localFilters, setLocalFilters] = useState<T>(deepClone(filters));
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setLocalFilters(deepClone(filters));
  }, [filters]);

  const updateLocal = (key: string, value: any) => {
    setLocalFilters((prev) => ({ ...prev, [key]: value }));
  };

  const hasUnsaved = isFiltersChanged(filters, localFilters);

  const hasActiveFilters = Object.entries(filters).some(([, value]) => {
    if (value === null || value === undefined || value === "") return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "boolean") return value !== false;
    if (typeof value === "object" && value !== null) return Object.keys(value).length > 0;
    return true;
  });

  const toggleFilters = () => setIsOpen((o) => !o);

  const panelBg   = useColorModeValue("white", "#0f2040");
  const shadow    = useColorModeValue("lg", "dark-lg");
  const textColor = useColorModeValue("gray.700", "whiteAlpha.900");
  const inputBg   = useColorModeValue("white", "#0a1628");
  const inputBorder = useColorModeValue("gray.200", "whiteAlpha.200");
  const menuBg    = useColorModeValue("white", "#0f2040");
  const menuItemHover = useColorModeValue("gray.100", "whiteAlpha.100");
  const isDark    = useColorModeValue(false, true);
  const popoverBg = useColorModeValue("white", "#0f2040");

  return (
    <Box position="sticky" display="flex" justifyContent="flex-start" zIndex={50} flexShrink={0} mt={10} mr={6}>
      <Box
        h={isOpen ? "100%" : "30px"}
        w={isOpen ? `${panelWidth}px` : "0px"}
        bg={panelBg}
        shadow={shadow}
        p={isOpen ? 6 : 0}
        transform={isOpen ? "translateX(0)" : "translateX(-120%)"}
        transition="transform 0.3s ease"
        overflowY="auto"
        position="relative"
      >
        <VStack spacing={4} align="stretch">
          {fields.map((field) => {
            const value = localFilters[field.key];

            switch (field.type) {
              case "text":
                return (
                  <Box key={field.key}>
                    <Text mb={1} color={textColor}>{field.label}</Text>
                    <Input
                      value={value ?? ""}
                      placeholder={field.placeholder}
                      bg={inputBg}
                      borderColor={inputBorder}
                      color={textColor}
                      onChange={(e) => updateLocal(field.key, e.target.value)}
                    />
                  </Box>
                );

              case "numberRange":
                return (
                  <Box key={field.key}>
                    <Text mb={1} color={textColor}>{field.label}</Text>
                    <VStack align="stretch">
                      <Input type="number" placeholder="Mínimo" bg={inputBg} borderColor={inputBorder} color={textColor}
                        value={value?.[0] ?? ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          updateLocal(field.key, [val === "" ? null : Number(val), value?.[1] ?? null]);
                        }}
                      />
                      <Input type="number" placeholder="Máximo" bg={inputBg} borderColor={inputBorder} color={textColor}
                        value={value?.[1] ?? ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          updateLocal(field.key, [value?.[0] ?? null, val === "" ? null : Number(val)]);
                        }}
                      />
                    </VStack>
                  </Box>
                );

              case "dateRange":
                return (
                  <Box key={field.key}>
                    <Text mb={1} color={textColor}>{field.label}</Text>
                    <Popover placement="bottom-start">
                      <PopoverTrigger>
                        <Button
                          leftIcon={<FiCalendar />}
                          rightIcon={<FiChevronDown />}
                          variant="outline"
                          w="100%"
                          justifyContent="space-between"
                          fontWeight="normal"
                          color={textColor}
                          borderColor={inputBorder}
                          bg={inputBg}
                        >
                          {value?.startDate && value?.endDate
                            ? `${format(new Date(value.startDate), "dd/MM/yyyy")} - ${format(new Date(value.endDate), "dd/MM/yyyy")}`
                            : "Selecionar período"}
                        </Button>
                      </PopoverTrigger>
                      <Portal>
                        <PopoverContent zIndex={9999} w="auto" bg={popoverBg}>
                          <PopoverArrow bg={popoverBg} />
                          <PopoverHeader color={textColor}>Selecione o período</PopoverHeader>
                          <PopoverCloseButton />
                          <PopoverBody p={0} sx={isDark ? {
                            ".rdrDateRangeWrapper, .rdrCalendarWrapper": { background: "#0f2040" },
                            ".rdrDefinedRangesWrapper": { background: "#0a1628", borderColor: "#1a3a5c" },
                            ".rdrStaticRange": { background: "#0a1628", borderColor: "#1a3a5c", color: "#e8edf2" },
                            ".rdrStaticRange:hover .rdrStaticRangeLabel, .rdrStaticRange:focus .rdrStaticRangeLabel": { background: "#112240" },
                            ".rdrStaticRangeLabel": { color: "#e8edf2" },
                            ".rdrStaticRangeSelected .rdrStaticRangeLabel": { color: "#009efb" },
                            ".rdrInputRanges": { background: "#0a1628" },
                            ".rdrInputRange": { color: "#e8edf2" },
                            ".rdrInputRangeInput": { background: "#0f2040", borderColor: "#1a3a5c", color: "#e8edf2" },
                            ".rdrDateDisplayWrapper": { background: "#0a1628" },
                            ".rdrDateDisplay": { background: "#0a1628" },
                            ".rdrDateDisplayItem": { background: "#0f2040", borderColor: "#1a3a5c", boxShadow: "none" },
                            ".rdrDateDisplayItem input": { color: "#e8edf2" },
                            ".rdrMonth": { background: "#0f2040" },
                            ".rdrMonthAndYearWrapper": { background: "#0f2040" },
                            ".rdrMonthAndYearPickers": { color: "#e8edf2" },
                            ".rdrMonthAndYearPickers select": { background: "#0a1628", color: "#e8edf2" },
                            ".rdrWeekDay": { color: "#718096" },
                            ".rdrDayNumber span": { color: "#e8edf2 !important" },
                            ".rdrDayPassive .rdrDayNumber span": { color: "#4a5568 !important" },
                            ".rdrDayDisabled": { background: "#0a1628 !important" },
                            ".rdrDayDisabled .rdrDayNumber span": { color: "#4a5568 !important" },
                            ".rdrDay:not(.rdrDayPassive) .rdrInRange ~ .rdrDayNumber span, .rdrDay:not(.rdrDayPassive) .rdrStartEdge ~ .rdrDayNumber span, .rdrDay:not(.rdrDayPassive) .rdrEndEdge ~ .rdrDayNumber span": { color: "#fff !important" },
                            ".rdrDayToday .rdrDayNumber span:after": { background: "#009efb" },
                            ".rdrNextPrevButton": { background: "#0a1628" },
                            ".rdrNextPrevButton:hover": { background: "#112240" },
                            ".rdrPprevButton i": { borderColor: "transparent #e8edf2 transparent transparent" },
                            ".rdrNextButton i":  { borderColor: "transparent transparent transparent #e8edf2" },
                            ".rdrMonthName": { color: "#e8edf2" },
                          } : {}}>
                            <DateRangePicker
                              ranges={[value || { startDate: new Date(), endDate: new Date(), key: "selection" }]}
                              onChange={({ selection }: any) => updateLocal(field.key, selection)}
                              moveRangeOnFirstSelection={false}
                              months={1}
                              direction="horizontal"
                            />
                          </PopoverBody>
                        </PopoverContent>
                      </Portal>
                    </Popover>
                  </Box>
                );

              case "multiSelect": {
                const selected: string[] = Array.isArray(value) ? value : [];
                const labelOf = (v: string) => field.options.find((o) => o.value === v)?.label ?? v;
                const toggle = (v: string) => {
                  updateLocal(field.key, selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
                };
                return (
                  <Box key={field.key}>
                    <Text mb={1} color={textColor}>{field.label}</Text>
                    <Menu closeOnSelect={false}>
                      <MenuButton as={Button} rightIcon={<ChevronDownIcon />} w="100%" textAlign="left"
                        fontWeight="normal" bg={inputBg} borderColor={inputBorder} color={textColor} variant="outline">
                        {selected.length === 0
                          ? "Selecionar"
                          : selected.length === 1
                            ? labelOf(selected[0])
                            : `${selected.length} selecionados`}
                      </MenuButton>
                      <MenuList maxH="200px" overflowY="auto" zIndex={9999} bg={menuBg}
                        sx={{ "&::-webkit-scrollbar": { width: "6px" }, "&::-webkit-scrollbar-thumb": { background: "blue.400", borderRadius: "24px" } }}>
                        {field.options.map((op) => {
                          const isSel = selected.includes(op.value);
                          return (
                            <MenuItem key={op.value} onClick={() => toggle(op.value)} color={textColor} _hover={{ bg: menuItemHover }}
                              icon={isSel ? <CheckIcon color="blue.500" boxSize={3} /> : <Box w="12px" />}>
                              <Text fontSize="sm" fontWeight={isSel ? "semibold" : "normal"}>{op.label}</Text>
                            </MenuItem>
                          );
                        })}
                      </MenuList>
                    </Menu>
                    {selected.length > 0 && (
                      <HStack mt={2} spacing={1} flexWrap="wrap">
                        {selected.map((v) => (
                          <Tag key={v} size="sm" colorScheme="blue" borderRadius="full">
                            <TagLabel>{labelOf(v)}</TagLabel>
                            <TagCloseButton onClick={() => toggle(v)} />
                          </Tag>
                        ))}
                      </HStack>
                    )}
                  </Box>
                );
              }

              case "select":
                return (
                  <Box key={field.key}>
                    <Text mb={1} color={textColor}>{field.label}</Text>
                    <RadioGroup value={value ?? ""} onChange={(v) => updateLocal(field.key, v)}>
                      <Stack spacing={1.5}>
                        {field.options.map((opt) => (
                          <Radio key={opt.value} value={opt.value} size="sm" colorScheme="blue">
                            <Text fontSize="sm" color={textColor}>{opt.label}</Text>
                          </Radio>
                        ))}
                      </Stack>
                    </RadioGroup>
                  </Box>
                );

              case "toggle":
                return (
                  <Box key={field.key}>
                    <HStack justify="space-between">
                      <Text>{field.label}</Text>
                      <Switch
                        isChecked={value ?? field.defaultValue ?? false}
                        onChange={(e) => updateLocal(field.key, e.target.checked)}
                      />
                    </HStack>
                  </Box>
                );

              default:
                return null;
            }
          })}

          <VStack spacing={2} pt={4}>
            <Button colorScheme="blue" w="100%" onClick={() => { onChange(localFilters); setIsOpen(false); }}>
              Aplicar filtros
            </Button>
            <Button variant="outline" w="100%" onClick={() => { onReset(); setIsOpen(false); }}>
              Limpar
            </Button>
          </VStack>
        </VStack>
      </Box>

      <Button
        position="absolute"
        w="120px"
        left={isOpen ? `${panelWidth}px` : "0"}
        borderRadius="0 9999px 9999px 0"
        bg="#009efb"
        color="white"
        _hover={{ bg: "#007bd4" }}
        _active={{ bg: "#006ab8" }}
        shadow="md"
        onClick={toggleFilters}
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        paddingRight="15px"
        transition="all 0.3s ease"
        opacity={isOpen ? 1 : 0.9}
        transform={isOpen ? "translateX(0)" : "translateX(-10px)"}
      >
        <Box as={FiFilter} size="23px" color="white" />
        {!isOpen && <Text ml={2} fontSize="sm" color="white">Filtros</Text>}

        {hasUnsaved && (
          <Tooltip label="Filtros não aplicados" placement="top" hasArrow>
            <Box borderRadius="50%" backgroundColor="orange.500" width="10px" height="10px" marginLeft="10px"
              animation={`${pulse} 1.5s infinite ease-in-out`} />
          </Tooltip>
        )}

        {!hasUnsaved && hasActiveFilters && (
          <Tooltip label="Filtros aplicados" placement="top" hasArrow>
            <Box borderRadius="50%" backgroundColor="green.400" width="10px" height="10px" marginLeft="10px"
              animation={`${pulse} 1.5s infinite ease-in-out`} />
          </Tooltip>
        )}
      </Button>
    </Box>
  );
};
